<template>
  <div class="banner">
    <div
      v-if="!isMobile"
      class="banner-pc"
    >
      <div class="download-app">
        <div class="download-banner">
          <picture class="hero">
            <source
              srcset="@/assets/images/pc/banner.webp"
              type="image/webp"
            />
            <img
              src="@/assets/images/pc/banner.jpg"
              width="3840"
              height="1560"
              fetchpriority="high"
              alt=""
            />
          </picture>
        </div>
        <button
          type="button"
          class="download-button"
          :disabled="loading || downloading"
          @click="goDownload()"
        >
          {{ storeReady ? '前往應用商店' : '立即下載' }}
        </button>
        <img
          src="@/assets/images/pc/banner1.jpg"
          class="banner1"
          width="2400"
          height="6376"
          loading="lazy"
          decoding="async"
          alt=""
        />
        <img
          src="@/assets/images/pc/banner2.png"
          class="banner2"
          width="2400"
          height="1668"
          loading="lazy"
          decoding="async"
          alt=""
        />
      </div>
    </div>
    <!-- h5 -->
    <BannerH5
      v-else
      class="banner-h5-wrap"
      :downloading="loading || downloading"
      :store-ready="storeReady"
      @download="goDownload()"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { parseUA, isSafariBrowser } from '@local-utils/common';
import { DownloadUrl } from '@local-utils/const';
import { useAttribution } from '@local-composable/common';
import { useRequest } from '@local-composable/request';
import { createFingerprint, ident_uid } from '@local-utils/burial-point';
import { showLoadingToast } from 'vant';
import CallApp from 'callapp-lib';
import BannerH5 from './banner-h5.vue';

const props = defineProps({
  isMobile: Boolean,
  loading: Boolean,
  channel: { type: String, default: '' },
  linkId: { type: Number, default: null },
  linkConfig: { type: Object, default: null },
  autoOpenAppStore: Boolean,
});
const downloading = ref(false);
const storeReady = ref(false);
const clickParam = {};
const attribution =
  props.channel && props.linkId
    ? useAttribution({ channel: props.channel, isToStore: false, clickParam })
    : null;
const visitorId = attribution
  ? new Promise((resolve) =>
      createFingerprint(() => {
        try {
          localStorage.setItem('USER_IDENT', ident_uid);
        } catch {
          // 禁用本地存储时仍使用本次生成的指纹上报。
        }
        resolve(ident_uid);
      }),
    )
  : null;

const reportEvent = async (eventType) => {
  if (!attribution) return;
  try {
    const visitor = await visitorId;
    if (!visitor) return;
    await useRequest(
      {
        url: '/v2/app_deep_link/event',
        data: {
          link_id: props.linkId,
          event_type: eventType,
          visitor_id: visitor,
          channel: props.channel,
        },
      },
      { toast: false, keepalive: true },
      { timeout: 3000 },
    ).post();
  } catch (error) {
    console.warn('Deep link event unavailable', error);
  }
};

let pageEvent;
onMounted(() => {
  pageEvent = reportEvent(3);
  return props.autoOpenAppStore && attribution ? goDownload() : pageEvent;
});

const goDownload = async () => {
  if (props.loading || downloading.value) return;
  downloading.value = true;
  const loading = showLoadingToast({
    duration: 0,
    forbidClick: true,
    message: 'loading...',
  });
  const eventRequest = reportEvent(4);
  let shouldOpenStore = storeReady.value;
  let urls;
  try {
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set('channel', props.channel);
    shareUrl.searchParams.set('type', 'new');
    await Promise.all([pageEvent, eventRequest]);
    if (!shouldOpenStore) {
      const appOpened = await tryOpenScheme();
      if (appOpened) return;
      if (appOpened === false) {
        // 超时不能确认用户已取消系统询问，只显示商店入口，等待再次点击。
        storeReady.value = true;
        return;
      }
      shouldOpenStore = true;
    }
    if (!attribution) return;
    if (clickParam.share_url !== shareUrl.href) {
      // 同渠道的旧归因可能属于其他链接，需重新请求以绑定当前 share_url。
      localStorage.removeItem(`attribution_click_data_${props.channel}`);
      attribution.attribution_id.value = '';
      clickParam.share_url = shareUrl.href;
    }
    urls = await attribution.toDownload();
  } catch (e) {
    console.error(e);
    if (!shouldOpenStore) storeReady.value = true;
  } finally {
    await Promise.all([pageEvent, eventRequest]);
    loading.close();
    try {
      if (shouldOpenStore) await launchDownload(urls);
    } finally {
      downloading.value = false;
    }
  }
};

function tryOpenScheme() {
  const ua = parseUA();
  const isMobileDevice =
    ua.ios || /Android/i.test(navigator.userAgent) || (ua.macos && navigator.maxTouchPoints > 1);
  // 按设备而非窗口宽度判断；PC 和 iOS Safari 直接走商店下载。
  if (!isMobileDevice || isSafariBrowser()) return null;
  const { global_type, func_type, data } = props.linkConfig || {};
  if (
    typeof global_type !== 'string' ||
    !global_type ||
    typeof func_type !== 'string' ||
    !func_type
  ) {
    return null;
  }
  const scheme = new URL(
    `seeronnet://main/inapp/${encodeURIComponent(global_type)}/${encodeURIComponent(func_type)}`,
  );
  for (const [key, value] of Object.entries(data && typeof data === 'object' ? data : {})) {
    scheme.searchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  }
  const caller = new CallApp({
    scheme: { protocol: 'seeronnet', host: 'main' },
    intent: { package: 'com.mmc.seer.onnet', scheme: 'seeronnet' },
    timeout: 3000,
    // 库内置降级也保留 Scheme，应用市场只能通过归因 click 打开。
    appstore: scheme.href,
    fallback: scheme.href,
    buildScheme: () => scheme.href,
  });
  return new Promise((resolve) => {
    let settled = false;
    let timer;
    // callapp-lib 只提供失败回调，以页面隐藏或离开作为唤起成功信号。
    const finish = (opened) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      resolve(opened);
    };
    const onPageHide = () => finish(true);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') finish(true);
    };
    window.addEventListener('pagehide', onPageHide, { once: true });
    document.addEventListener('visibilitychange', onVisibility);
    // 库在任意 visibilitychange 时都会取消超时，独立计时避免失败后一直等待。
    timer = setTimeout(() => finish(false), 3000);
    try {
      caller.open({ path: scheme.pathname.slice(1), callback: () => finish(false) });
    } catch (error) {
      console.warn('Scheme launch unavailable', error);
      finish(false);
    }
  });
}

function launchDownload(urls = DownloadUrl) {
  const ua = parseUA();
  const url = ua.ios || ua.macos ? urls.ios : urls.android;
  const launcher = window.AppLaunchSDK?.createDownloadLauncher?.({
    timeout: 3000,
    fallbackInWebViewOnly: true,
  });
  if (launcher) return launcher.open({ downloadUrl: url });
  window.location.href = url;
}

defineExpose({ goDownload, storeReady, downloading });
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common.scss';

.banner {
  max-width: $pc-max-width;
  background-color: #ffffff;
  overflow: hidden;
  padding-bottom: 2rem;

  .download-app {
    .download-banner {
      position: relative;
      box-sizing: border-box;
      width: 100%;
      aspect-ratio: 3840 / 1560;

      .hero {
        position: absolute;
        inset: 0;
        pointer-events: none;

        img {
          display: block;
          width: 100%;
          height: auto;
        }
      }
    }

    .download-button {
      position: relative;
      z-index: 6;
      width: 4rem;
      height: 0.8rem;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 0.56rem;
      margin: -3.2rem auto 0.5rem;
      padding: 0 0.22rem;
      border-radius: 0.12rem;
      background: linear-gradient(to bottom, #ffe7a8, #fff5d7);
      border: 1px solid #ffe7aa;
      cursor: pointer;
      font-size: 0.28rem;
      line-height: 1.2;
      color: #1a1208;
    }

    .banner1 {
      position: relative;
      z-index: 5;
      display: block;
      width: 62.5%;
      height: auto;
      aspect-ratio: 2400 / 6376;
      margin: 0 auto;
    }

    .banner2 {
      display: block;
      width: 62.5%;
      height: auto;
      aspect-ratio: 2400 / 1668;
      margin: 1.5rem auto 0;
    }
  }

  .more {
    display: none;
  }

  .banner-h5-wrap {
    display: none;
  }
}

@media (max-width: 768px) {
  .banner {
    max-width: none;
    padding-bottom: 0;

    .banner-pc {
      display: none;
    }

    .banner-h5-wrap {
      display: block;
    }
  }
}
</style>
